import { type AxiosProgressEvent, isCancel } from 'axios';
import {
  AbortedError,
  CreateMediaError,
  InvalidStateError,
  UnknownError,
  UnreachableError,
  UploadChunkError,
} from 'errors';
import { aws as awsServices, media as mediaServices } from 'services';
import { calculateFinalSha256Checksum, calculateSha256Checksum } from 'utils';
import { type CreatedMediaDataObject, type MediaDataObject } from 'models/services';

export type UploadSubscriber = {
  onAbort?: () => void;
  onError?: (error: Error) => void;
  onProgress?: ({ percent }: { percent: number }) => void;
  onSuccess?: (media: CreatedMediaDataObject) => void;
};

export type SuccessfullyUploadedChunk = {
  checksum: string;
  chunkNumber: number;
  etag: string;
};

type Options = {
  mimeType: string;
  name: string;
  ref: string;
  type: string;
};

type Status = 'aborted' | 'aborting' | 'done' | 'error' | 'initial' | 'uploading';

// TODO: Separate methods to some logical structure so this file is not too big
class UploadFile {
  #currentState:
    | {
        data: CreatedMediaDataObject;
        status: 'done';
      }
    | {
        error: Error;
        status: 'error';
      }
    | {
        status: 'aborted';
      }
    | {
        status: 'aborting';
      }
    | {
        status: 'initial';
      }
    | {
        status: 'uploading';
      } = { status: 'initial' };

  get state() {
    return this.#currentState;
  }

  #mediaResponse?: MediaDataObject;

  get #maxRetries() {
    return 3;
  }

  get #retryWaitTime() {
    return 1000;
  }

  get file() {
    return this.#file;
  }

  readonly #file: File;

  readonly #options: Options;
  readonly #controller: AbortController;

  readonly #successfullyUploadedChunks: SuccessfullyUploadedChunk[] = [];
  #subscribers: UploadSubscriber[] = [];

  constructor(file: File, options: Options) {
    this.#controller = new AbortController();
    this.#options = options;
    this.#file = file;
  }

  upload() {
    if (!this.#stateGuard({ allowedStates: ['initial'] })) {
      throw new InvalidStateError({ action: 'upload', status: this.state.status });
    }

    void this.#handleAsync(async () => {
      await this.#uploadFile();
    });
  }

  retry() {
    if (!this.#stateGuard({ allowedStates: ['error'] })) {
      throw new InvalidStateError({ action: 'retry', status: this.state.status });
    }

    void this.#handleAsync(async () => {
      await this.#uploadFile();
    });
  }

  abort() {
    if (!this.#stateGuard({ allowedStates: ['uploading', 'error', 'initial'] })) {
      throw new InvalidStateError({ action: 'abort', status: this.state.status });
    }

    void this.#handleAsync(async () => {
      await this.#abortFileUpload();
    });
  }

  subscribe(subscriber: UploadSubscriber) {
    this.#subscribers.push(subscriber);
  }

  unsubscribe(subscriber: UploadSubscriber) {
    this.#subscribers = this.#subscribers.filter((sub) => sub !== subscriber);
  }

  async #uploadFile() {
    this.#currentState.status = 'uploading';

    const media = await this.#createChunkedUpload();

    await this.#uploadRemainingChunks(media);

    const createdMedia = await this.#completeUpload(media);

    this.#onSuccessHandler(createdMedia);
  }

  async #createChunkedUpload() {
    if (!this.#mediaResponse) {
      try {
        const response = await mediaServices.createChunkedUpload({
          body: {
            mimeType: this.#options.mimeType,
            type: this.#options.type,
            name: this.#file.name,
            ref: this.#options.ref,
          },
          config: {
            signal: this.#controller.signal,
          },
        });

        this.#mediaResponse = response.data;
      } catch (error) {
        throw new CreateMediaError(error);
      }
    }

    return this.#mediaResponse;
  }

  async #uploadRemainingChunks(media: MediaDataObject) {
    const numberOfUploadedChunks = this.#successfullyUploadedChunks.length;
    const numberOfChunks = this.#file.size / media.meta.chunkedUpload.chunkSize;
    const totalChunks = Math.ceil(numberOfChunks);

    if (numberOfUploadedChunks < totalChunks) {
      await this.#uploadChunks({
        media,
        total: totalChunks,
        index: numberOfUploadedChunks,
      });
    }
  }

  async #uploadChunks({
    index: startIndex,
    media,
    total,
  }: {
    index: number;
    media: MediaDataObject;
    total: number;
  }): Promise<void> {
    for (let index = startIndex; index < total; index++) {
      const chunk = this.#file.slice(
        index * media.meta.chunkedUpload.chunkSize,
        (index + 1) * media.meta.chunkedUpload.chunkSize,
      );

      // eslint-disable-next-line no-await-in-loop -- we need to wait for the every response
      const response = await this.#uploadChunk({ chunk, media });

      this.#successfullyUploadedChunks.push({
        checksum: response.data.checksum,
        chunkNumber: index + 1,
        etag: response.data.etag,
      });
    }
  }

  async #uploadChunk({
    chunk,
    media,
  }: {
    chunk: Blob;
    media: MediaDataObject;
  }): Promise<{ data: { checksum: string; etag: string } }> {
    let retries = 0;
    const checksum = await calculateSha256Checksum(chunk);

    do {
      try {
        // eslint-disable-next-line no-await-in-loop --  disable no-await-in-loop because we need to handle each chunk sequentially
        const { data: uploadInfo } = await mediaServices.getUploadInfo({
          body: {
            checksum,
            chunkNumber: this.#successfullyUploadedChunks.length + 1,
          },
          mediaId: media._id,
          config: {
            signal: this.#controller.signal,
          },
        });

        // eslint-disable-next-line no-await-in-loop --  disable no-await-in-loop because we need to handle each chunk sequentially
        return await (uploadInfo.type === 'media'
          ? this.#handleMediaUpload({ chunk, checksum, media })
          : this.#handleAwsUpload({ chunk, checksum, url: uploadInfo.url }));
      } catch (error) {
        if (error instanceof Error && isCancel(error)) {
          throw new AbortedError();
        }

        if (++retries === this.#maxRetries) {
          throw new UploadChunkError(this.#successfullyUploadedChunks.length);
        }

        // Adding some delay between retries to prevent overwhelming the server or network error and increase the chance of possible network issues to resolve
        // eslint-disable-next-line no-await-in-loop -- we want to wait for the delay
        await new Promise((resolve) => {
          setTimeout(resolve, this.#retryWaitTime);
        });
      }
    } while (retries < this.#maxRetries);

    throw new UnreachableError();
  }

  async #handleAwsUpload({
    checksum,
    chunk,
    url,
  }: {
    checksum: string;
    chunk: Blob;
    url: string;
  }): Promise<{ data: { checksum: string; etag: string } }> {
    const { headers } = await awsServices.uploadChunk({
      body: chunk,
      config: {
        headers: {
          'x-amz-checksum-sha256': checksum,
        },
        onUploadProgress: this.#handleUploadProgress,
        signal: this.#controller.signal,
      },
      url,
    });

    if (headers.etag && typeof headers.etag === 'string') {
      return { data: { etag: headers.etag, checksum } };
    }

    throw new UnreachableError();
  }

  async #handleMediaUpload({
    checksum,
    chunk,
    media,
  }: {
    checksum: string;
    chunk: Blob;
    media: MediaDataObject;
  }): Promise<{ data: { checksum: string; etag: string } }> {
    const formData = new FormData();
    formData.append('chunkNumber', (this.#successfullyUploadedChunks.length + 1).toString());
    formData.append('checksum', checksum);
    formData.append('file', chunk);

    const { data } = await mediaServices.uploadChunk({
      body: formData,
      mediaId: media._id,
      config: {
        onUploadProgress: this.#handleUploadProgress,
        signal: this.#controller.signal,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    });

    return { data: { etag: data.ETag, checksum } };
  }

  async #completeUpload(media: MediaDataObject) {
    const checksums = this.#successfullyUploadedChunks.map((chunk) => chunk.checksum);

    const finalChecksum = await calculateFinalSha256Checksum({ checksums });

    const response = await mediaServices.completeUpload({
      mediaId: media._id,
      body: {
        checksum: finalChecksum,
        chunks: this.#successfullyUploadedChunks,
      },
      config: {
        signal: this.#controller.signal,
      },
    });

    return response.data;
  }

  async #abortFileUpload() {
    this.#currentState = { status: 'aborting' };
    this.#controller.abort();

    if (this.#mediaResponse) {
      await mediaServices.abortUpload({
        mediaId: this.#mediaResponse._id,
      });
    }

    this.#onAbortHandler();
  }

  #stateGuard({ allowedStates }: { allowedStates: Status[] }) {
    return allowedStates.includes(this.#currentState.status);
  }

  readonly #handleUploadProgress = (progressEvent: AxiosProgressEvent) => {
    if (!this.#mediaResponse) {
      throw new UnreachableError();
    }

    const uploadedChunksSize =
      this.#successfullyUploadedChunks.length * this.#mediaResponse.meta.chunkedUpload.chunkSize;

    const loaded = progressEvent.loaded;
    const totalSize = this.#file.size;

    const percent = ((uploadedChunksSize + loaded) / totalSize) * 100;

    this.#onProgressHandler(percent);
  };

  async #handleAsync(handler: () => Promise<void>) {
    try {
      await handler();
    } catch (error) {
      if (error instanceof Error) {
        this.#onErrorHandler(error);
      } else {
        this.#onErrorHandler(new UnknownError(error));
      }
    }
  }

  #onErrorHandler(error: Error) {
    if (error instanceof AbortedError) {
      return;
    }

    this.#currentState = { status: 'error', error };

    let isHandled = false;

    for (const subscriber of this.#subscribers) {
      try {
        subscriber.onError?.(error);
        isHandled &&= Boolean(subscriber.onError);
      } catch {}
    }

    if (!isHandled) {
      throw error;
    }
  }

  #onSuccessHandler(media: CreatedMediaDataObject) {
    this.#currentState = { status: 'done', data: media };

    for (const subscriber of this.#subscribers) {
      try {
        subscriber.onSuccess?.(media);
      } catch {}
    }
  }

  #onProgressHandler(percent: number) {
    for (const subscriber of this.#subscribers) {
      try {
        subscriber.onProgress?.({ percent });
      } catch {}
    }
  }

  #onAbortHandler() {
    this.#currentState = { status: 'aborted' };

    for (const subscriber of this.#subscribers) {
      try {
        subscriber.onAbort?.();
      } catch {}
    }
  }
}

export default UploadFile;
